import { ResponseFormat } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/conversations_pb";
import Sentry from "@mirage/shared/sentry";

import type { context_engine } from "@dropbox/api-v2-client/types/dropbox_types";
import type {
  Author,
  Channel,
  ChannelType,
  Conversation,
  ConversationClientContext,
  ConversationMessage,
  ConversationMessageJsonContent,
  ConversationMetadata,
  DocumentInfo,
  IconResource,
  ToolCall,
} from "@mirage/service-conversation/types";
import { tagged } from "@mirage/service-logging";

const logger = tagged("ceConverters");

// This file contains converters for converting CE types to service-conversation types.

/**
 * Transforms a CE conversation to metadata.
 * Returns undefined if the conversation is invalid.
 */
export function transformCEConversationToMetadata(
  ceConversation: context_engine.UserConversation,
): ConversationMetadata | undefined {
  try {
    return {
      serverConversationId: assertNotUndefined(ceConversation, "id"),
      created: new Date(assertNotUndefined(ceConversation, "created_ts")),
      updated: new Date(assertNotUndefined(ceConversation, "updated_ts")),
      title: ceConversation.title || "",
      initialAgent: ceConversation.initial_agent || undefined,
      preview: ceConversation.preview || "",
      clientContext: transformCEClientContextToConversationClientContext(
        ceConversation.client_context,
      ),
      stackId: ceConversation.stack_id || undefined,
    };
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  }
}

/**
 * Transforms a list of CE conversations to a list of metadata.
 * This will automatically filter out any conversations that don't have a valid metadata.
 */
export function transformCEConversationsToMetadatas(
  ceConversations: context_engine.UserConversation[],
): ConversationMetadata[] {
  return ceConversations
    .map(transformCEConversationToMetadata)
    .filter(
      (metadata): metadata is ConversationMetadata => metadata !== undefined,
    );
}

/**
 * Transforms a CE ClientContext to a ConversationClientContext.
 * If client_context is undefined or missing required fields, returns undefined.
 */
export function transformCEClientContextToConversationClientContext(
  clientContext?: context_engine.ClientContext,
): ConversationClientContext | undefined {
  if (!clientContext) {
    return undefined;
  }

  return {
    timezone: clientContext.timezone || "",
    clientScenario: clientContext.client_scenario || "",
    clientScenarioId: clientContext.client_scenario_id || "",
  };
}

/**
 * Transforms a CE conversation and its messages to a Conversation + metadata.
 * This will automatically filter out any messages that don't have a valid message type.
 */
export function transformCEConversationToConversation(
  ceConversation: context_engine.UserConversation,
  ceMessages: context_engine.ConversationsChatMessage[],
): { conversation: Conversation; metadata: ConversationMetadata } {
  const metadata = transformCEConversationToMetadata(ceConversation);
  if (!metadata) {
    throw new Error("Metadata is undefined");
  }
  const messages = ceMessages
    .map((ceMessage) =>
      transformCEConversationMessageToConversationMessage(ceMessage),
    )
    .filter((message): message is ConversationMessage => message !== undefined)
    .reverse(); // CE conversation messages are sorted by most recent first, we want to reverse them
  const conversation: Conversation = {
    messages,
    suggestedQueries: [], // only updated after each server response
    markdownDraft: ceConversation.draft?.content,
    serverConversationId: ceConversation.id,
    writeAgentPayload: ceConversation.draft?.write_agent_payload,
    clientContext: transformCEClientContextToConversationClientContext(
      ceConversation.client_context,
    ),
    stackId: ceConversation.stack_id || undefined,
  };
  return {
    conversation,
    metadata,
  };
}

/**
 * Parses JSON content from an assistant message text.
 * Returns the parsed JSON content and potentially modified message text.
 */
export function parseAssistantMessageJsonContent(messageText: string): {
  jsonContent?: ConversationMessageJsonContent;
  modifiedText?: string;
} {
  try {
    const parsedContent = JSON.parse(messageText);
    const jsonContent = {
      content: parsedContent.content || "",
      followUpSuggestions: parsedContent.follow_up_suggestions || [],
      draftModifications: parsedContent.draft_modifications || "",
      calendarEvents: parsedContent.items || [],
    };
    // If there are draft modifications, use them as the text instead of the stringified JSON
    const modifiedText = jsonContent.draftModifications || undefined;
    return {
      jsonContent,
      modifiedText,
    };
  } catch (e) {
    // If parsing fails, return empty object
    return {};
  }
}

/**
 * Transforms a CE conversation message to a ConversationMessage.
 * Returns undefined if the message is invalid.
 */
function transformCEConversationMessageToConversationMessage(
  ceMessage: context_engine.ConversationsChatMessage,
): ConversationMessage | undefined {
  try {
    const messageType = transformCEMessageTypeToConversationMessageType(
      assertNotUndefined(ceMessage, "message_type"),
    );

    let jsonContent: ConversationMessageJsonContent | undefined;
    if (
      ceMessage.message_type?.[".tag"] === "assistant_message" &&
      ceMessage.message_type?.format?.[".tag"] === "json" &&
      ceMessage.message_type?.text &&
      messageType.case === "assistantMessage"
    ) {
      const { jsonContent: parsedJsonContent, modifiedText } =
        parseAssistantMessageJsonContent(ceMessage.message_type.text);
      jsonContent = parsedJsonContent;

      // Update the message text if we have modified text from JSON parsing
      if (modifiedText && messageType.case === "assistantMessage") {
        messageType.value.text = modifiedText;
      }
    }

    return {
      id: assertNotUndefined(ceMessage, "id"),
      timestamp: new Date(assertNotUndefined(ceMessage, "timestamp")),
      status: "success",
      messageType,
      jsonContent,
    };
  } catch (error) {
    logger.error(
      `$$Error transforming CE conversation message to ConversationMessage: ${error}`,
    );
    Sentry.captureException(error);
    return undefined;
  }
}

/**
 * Transforms a CE message type to a ConversationMessage.
 * Throws an error if the message type is invalid.
 */
function transformCEMessageTypeToConversationMessageType(
  ceMessageType: context_engine.message_type_union,
): ConversationMessage["messageType"] {
  switch (ceMessageType[".tag"]) {
    case "system_message":
      return {
        case: "systemMessage",
        value: {
          text: assertNotUndefined(ceMessageType, "text"),
        },
      };
    case "user_message":
      return {
        case: "userMessage",
        value: {
          text: assertNotUndefined(ceMessageType, "text"),
          rephrased: ceMessageType.rephrased || "",
          attachments: (ceMessageType.attachments || []).map(
            transformCEAttachmentToDocumentInfo,
          ),
        },
      };
    case "assistant_message":
      return {
        case: "assistantMessage",
        value: {
          text: assertNotUndefined(ceMessageType, "text"),
          cited: (ceMessageType.cited || []).map(
            transformCEAttachmentToDocumentInfo,
          ),
          context: (ceMessageType.context || []).map(
            transformCEAttachmentToDocumentInfo,
          ),
          isCached: assertNotUndefined(ceMessageType, "is_cached"),
          agentId: ceMessageType.agent_id,
          format: transformCEResponseFormatToResponseFormat(
            ceMessageType.format,
          ),
          toolCalls: transformCEMessageTypeToolCallsToToolCalls(
            ceMessageType.tool_calls,
          ),
        },
      };
    case "tool_message":
      return {
        case: "toolMessage",
        value: {
          text: ceMessageType.content || "",
          toolCallId: ceMessageType.tool_call_id || "",
          name: ceMessageType.name || "",
          content: ceMessageType.content || "",
          arguments: ceMessageType.arguments,
        },
      };
    case "other":
      return {
        case: undefined,
        value: undefined,
      };
    default:
      ceMessageType[".tag"] satisfies never;
      throw new Error(`Unknown message type: ${ceMessageType[".tag"]}`);
  }
}

/**
 * Transforms a CE author to an Author.
 * Throws an error if the author is invalid.
 */
function transformCEAuthorToAuthor(ceAuthor: context_engine.CE_Author): Author {
  return {
    display_name: assertNotUndefined(ceAuthor, "display_name"),
    email: assertNotUndefined(ceAuthor, "email"),
    profile_image_url: assertNotUndefined(ceAuthor, "profile_image_url"),
    alias_handle: assertNotUndefined(ceAuthor, "alias_handle"),
    profile_link: assertNotUndefined(ceAuthor, "profile_link"),
  };
}

/**
 * Transforms a CE channel type to a ChannelType.
 * Returns channel_type_unset for unknown types.
 */
function transformCEChannelTypeToChannelType(
  ceChannelType: context_engine.CE_ChannelType,
): ChannelType {
  switch (ceChannelType[".tag"]) {
    case "channel_type_unset":
      return { ".tag": "channel_type_unset" };
    case "channel_type_public":
      return { ".tag": "channel_type_public" };
    case "channel_type_private":
      return { ".tag": "channel_type_private" };
    case "channel_type_dm":
      return { ".tag": "channel_type_dm" };
    default:
      return { ".tag": "channel_type_unset" };
  }
}

/**
 * Transforms a CE channel to a Channel.
 * Throws an error if the channel is invalid.
 */
function transformCEChannelToChannel(
  ceChannel: context_engine.CE_Channel | undefined,
): Channel | undefined {
  if (!ceChannel) {
    return undefined;
  }
  return {
    id: assertNotUndefined(ceChannel, "id"),
    name: assertNotUndefined(ceChannel, "name"),
    url: assertNotUndefined(ceChannel, "url"),
    created_at_ms: assertNotUndefined(ceChannel, "created_at_ms"),
    type: transformCEChannelTypeToChannelType(
      assertNotUndefined(ceChannel, "type"),
    ),
    members: ceChannel.members?.map(transformCEAuthorToAuthor),
  };
}

/**
 * Transforms a CE attachment to a DocumentInfo.
 * Throws an error if the attachment is invalid.
 */
export function transformCEAttachmentToDocumentInfo(
  ceAttachment: context_engine.DocumentInfo,
): DocumentInfo {
  return {
    url: assertNotUndefined(ceAttachment, "url"),
    title: assertNotUndefined(ceAttachment, "title"),
    lastUpdatedMs: assertNotUndefined(ceAttachment, "last_updated_ms"),
    brandedType: assertNotUndefined(ceAttachment, "branded_type"),
    body: assertNotUndefined(ceAttachment, "body"),
    ownerId: assertNotUndefined(ceAttachment, "owner_id"),
    thirdPartyId: assertNotUndefined(ceAttachment, "third_party_id"),
    relevanceScore: assertNotUndefined(ceAttachment, "relevance_score"),
    uuid: assertNotUndefined(ceAttachment, "uuid"),
    index: assertNotUndefined(ceAttachment, "index"),

    channel: transformCEChannelToChannel(ceAttachment.channel),
    connectorInfo: transformCEConnectorInfoToConnectorInfo(
      ceAttachment.connector_info,
    ),
    fileTypeInfo: transformCEFileTypeInfoToFileTypeInfo(
      ceAttachment.file_type_info,
    ),
  };
}

/**
 * Transforms a CE connector info to a ConnectorInfo.
 * Throws an error if the connector info is invalid.
 */
function transformCEConnectorInfoToConnectorInfo(
  ceConnectorInfo: context_engine.CE_ConnectorInfo | undefined,
): DocumentInfo["connectorInfo"] | undefined {
  if (!ceConnectorInfo) {
    return undefined;
  }
  return {
    connectorId: assertNotUndefined(ceConnectorInfo, "connector_id"),
    connectorType: assertNotUndefined(ceConnectorInfo, "connector_type"),
    displayName: assertNotUndefined(ceConnectorInfo, "display_name"),
    platform: assertNotUndefined(ceConnectorInfo, "platform"),
    connectorName: assertNotUndefined(ceConnectorInfo, "connector_name"),
    connectorIconUrl: assertNotUndefined(ceConnectorInfo, "connector_icon_url"),
    icon: transformCEIconResourceToIconResource(ceConnectorInfo.icon),
  };
}

/**
 * Transforms a CE icon resource to an IconResource.
 * Throws an error if the icon resource is invalid.
 */
function transformCEIconResourceToIconResource(
  ceIconResource: context_engine.CE_IconResource | undefined,
): IconResource | undefined {
  if (!ceIconResource) {
    return undefined;
  }
  return {
    lightUrl: assertNotUndefined(ceIconResource, "light_url"),
    darkUrl: assertNotUndefined(ceIconResource, "dark_url"),
    lightPngUrl: assertNotUndefined(ceIconResource, "light_png_url"),
    darkPngUrl: assertNotUndefined(ceIconResource, "dark_png_url"),
  };
}

/**
 * Transforms a CE file type info to a FileTypeInfo.
 * Throws an error if the file type info is invalid.
 */
function transformCEFileTypeInfoToFileTypeInfo(
  ceFileTypeInfo: context_engine.CE_FileTypeInfo | undefined,
): DocumentInfo["fileTypeInfo"] | undefined {
  if (!ceFileTypeInfo) {
    return undefined;
  }
  return {
    id: assertNotUndefined(ceFileTypeInfo, "id"),
    displayName: assertNotUndefined(ceFileTypeInfo, "display_name"),
    icon: transformCEIconResourceToIconResource(ceFileTypeInfo.icon),
  };
}

/**
 * Transforms a CE response format to a ResponseFormat.
 * Returns undefined if the response format is invalid.
 */
function transformCEResponseFormatToResponseFormat(
  ceResponseFormat: context_engine.ResponseFormat | undefined,
): ResponseFormat | undefined {
  if (!ceResponseFormat) {
    return undefined;
  }
  switch (ceResponseFormat[".tag"]) {
    case "markdown":
      return ResponseFormat.MARKDOWN;
    case "json":
      return ResponseFormat.JSON;
    case "other":
      return undefined;
    default:
      ceResponseFormat[".tag"] satisfies never;
      return undefined;
  }
}

/**
 * Transforms a list of CE tool calls to a list of ToolCall.
 * Returns undefined if the tool calls are invalid.
 */
export function transformCEMessageTypeToolCallsToToolCalls(
  ceToolCalls: context_engine.ConversationsToolCall[] | undefined,
): ToolCall[] | undefined {
  if (!ceToolCalls) {
    return undefined;
  }
  const toolCalls: ToolCall[] = [];
  for (const ceToolCall of ceToolCalls) {
    toolCalls.push({
      id: assertNotUndefined(ceToolCall, "id"),
      name: assertNotUndefined(ceToolCall, "name"),
      arguments: assertNotUndefined(ceToolCall, "arguments"),
      type: assertNotUndefined(ceToolCall, "type"),
    });
  }
  return toolCalls;
}

/**
 * Asserts that a value is not undefined or null.
 * Throws an error if the value is undefined or null.
 */
function assertNotUndefined<T, K extends keyof T>(
  value: T,
  key: K,
): NonNullable<T[K]> {
  if (value[key] === undefined) {
    throw new Error(`Value is undefined for key ${String(key)}`);
  }
  if (value[key] === null) {
    throw new Error(`Value is null for key ${String(key)}`);
  }
  return value[key];
}
